
import React from "react";
import styles from "../assets/style.module.css";
import * as ReactRouterDom from "react-router-dom";
import { IHostWebsite, IMember } from "../../../../services";
import LanguageDropdown from "../../../../shared/LanguageDropdown";
import { useComponentStyle } from "../../../../hooks/useComponentStyle";
import { CacheManager, Utils } from "../../../../utils";
import { Classname, DateHelper, StringHelper } from "@rivtn/noseur";
import { i18nManager } from "../../../../i18n";
import Home from "./Home";
import About from "./About";
import Events from "./Events";
import Blog from "./Blog";
import Tournaments from "./Tournaments";

// ─── TYPES ────────────────────────────────────────────────────────────────────
type PageId = "home" | "blog" | "merch" | "events" | "tournaments" | "about" | "dashboard";
type TournamentStatus = "upcoming" | "ongoing" | "past";
type EventStatus = "upcoming" | "ongoing" | "past";
type LanguageCode = "en" | "pt" | "es" | "fr" | "de";

interface Language { code: LanguageCode; label: string; flag: string; }
interface SocialLinks { instagram?: string; twitter?: string; youtube?: string; facebook?: string; discord?: string; tiktok?: string; }

interface TournamentEdition {
    edition: number;
    year: number;
    winner: string;
    prizePool: string;
}
interface Tournament {
    id: number;
    name: string;
    logo: string;
    banner: string;
    status: TournamentStatus;
    prizePool: string;
    playersPerTeam: number;
    game: string;
    date: string;
    registeredTeams: number;
    maxTeams: number;
    registerLink: string;
    editions: TournamentEdition[];
    description: string;
    location: string;
}

interface TEvent {
    id: number;
    name: string;
    flier: string;
    description: string;
    date: string;
    status: EventStatus;
    registerLink: string;
    location: string;
    category: string;
}

interface MerchItem { id: number; name: string; price: number; img: string; badge: string | null; }

interface StaffMember {
    id: number;
    displayName: string;
    role: string;
    bio: string;
    passport: string;
    fullBody: string;
    country: string;
    state: string;
    social: SocialLinks;
    profileLink: string;
}

interface Review {
    id: number;
    author: string;
    avatar: string;
    rating: number;
    text: string;
    tournament: string;
    date: string;
}

// ─── CSS ─────────────────────────────────────────────────────────────────────
const css: string = ``;

// ─── STATIC DATA ──────────────────────────────────────────────────────────────

const LANGUAGES: Language[] = [
    { code: "en", label: "English", flag: "🇬🇧" },
    { code: "pt", label: "Português", flag: "🇧🇷" },
    { code: "es", label: "Español", flag: "🇪🇸" },
    { code: "fr", label: "Français", flag: "🇫🇷" },
    { code: "de", label: "Deutsch", flag: "🇩🇪" },
];

const ORG = {
    name: "NEXUS ESPORTS", short: "NXS",
    tagline: "Where Champions Are Made",
    description: "Nexus Esports is a premier tournament organizer founded in 2019, dedicated to delivering world-class competitive gaming events. From grassroots cups to international championships, we create unforgettable experiences for players and spectators alike.",
    logoImg: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=200&q=80",
    heroImg: "https://images.unsplash.com/photo-1493711662062-fa541adb3fc8?w=700&q=80",
    address: "42 Arena Boulevard, Tech District, São Paulo, SP 01310-100, Brazil",
    openingHours: [
        { day: "Monday – Friday", time: "09:00 – 18:00" },
        { day: "Saturday", time: "10:00 – 16:00" },
        { day: "Sunday", time: "Closed" },
    ],
    emails: ["events@nexusesports.gg", "support@nexusesports.gg"],
    phones: ["+55 11 4002-8922", "+55 11 4002-8900"],
    social: { instagram: "@nexusesports", twitter: "@NexusEsports_GG", youtube: "Nexus Esports", discord: "discord.gg/nexus", facebook: "fb.com/nexusesports" },
    stats: { tournaments: 48, totalPlayers: 12400, totalPrize: "$2.4M", countries: 38 },
    sponsors: ["NVIDIA", "Red Bull", "HyperX", "Samsung", "Discord", "Epic Games"],
};

const TOURNAMENTS: Tournament[] = [
    {
        id: 1, name: "Nexus World Championship", logo: "🏆", status: "ongoing",
        banner: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=800&q=80",
        prizePool: "$100,000", playersPerTeam: 5, game: "League of Legends",
        date: "Feb 28 – Mar 10, 2026", registeredTeams: 28, maxTeams: 32,
        registerLink: "#", location: "São Paulo, Brazil",
        description: "The flagship annual championship bringing together the best teams from across the globe.",
        editions: [
            { edition: 4, year: 2025, winner: "Team Alpha", prizePool: "$80,000" },
            { edition: 3, year: 2024, winner: "Nova Esports", prizePool: "$60,000" },
            { edition: 2, year: 2023, winner: "Team Alpha", prizePool: "$40,000" },
            { edition: 1, year: 2022, winner: "Phantom GG", prizePool: "$20,000" },
        ]
    },
    {
        id: 2, name: "Spring Invitational Cup", logo: "🥇", status: "upcoming",
        banner: "https://images.unsplash.com/photo-1493711662062-fa541adb3fc8?w=800&q=80",
        prizePool: "$25,000", playersPerTeam: 5, game: "Valorant",
        date: "Mar 15–20, 2026", registeredTeams: 12, maxTeams: 16,
        registerLink: "#", location: "Online",
        description: "Open-bracket spring invitational for rising teams looking to make their mark.",
        editions: [
            { edition: 2, year: 2025, winner: "Clutch Kings", prizePool: "$20,000" },
            { edition: 1, year: 2024, winner: "Rising Stars", prizePool: "$15,000" },
        ]
    },
    {
        id: 3, name: "Nexus Amateur League S3", logo: "⭐", status: "upcoming",
        banner: "https://images.unsplash.com/photo-1511512578047-dfb367046420?w=800&q=80",
        prizePool: "$10,000", playersPerTeam: 5, game: "CS2",
        date: "Apr 1–30, 2026", registeredTeams: 6, maxTeams: 24,
        registerLink: "#", location: "Online",
        description: "A month-long amateur league for teams looking to build experience in a structured format.",
        editions: [
            { edition: 2, year: 2025, winner: "Dark Matter", prizePool: "$8,000" },
        ]
    },
    {
        id: 4, name: "Grand Prix Open", logo: "🎖️", status: "past",
        banner: "https://images.unsplash.com/photo-1560253023-3ec5d502959f?w=800&q=80",
        prizePool: "$50,000", playersPerTeam: 5, game: "Dota 2",
        date: "Jan 10–15, 2026", registeredTeams: 32, maxTeams: 32,
        registerLink: "#", location: "Rio de Janeiro, Brazil",
        description: "A high-octane open tournament concluded with record viewership and attendance.",
        editions: [
            { edition: 3, year: 2026, winner: "Storm Force", prizePool: "$50,000" },
            { edition: 2, year: 2025, winner: "Iron Legion", prizePool: "$35,000" },
            { edition: 1, year: 2024, winner: "Storm Force", prizePool: "$25,000" },
        ]
    },
    {
        id: 5, name: "Nexus Cup Qualifiers", logo: "🏅", status: "past",
        banner: "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=800&q=80",
        prizePool: "$5,000", playersPerTeam: 5, game: "Fortnite",
        date: "Dec 5–8, 2025", registeredTeams: 48, maxTeams: 48,
        registerLink: "#", location: "Online",
        description: "Qualifier event for the main Nexus Cup, attracting hundreds of teams worldwide.",
        editions: [{ edition: 1, year: 2025, winner: "Solo Kings", prizePool: "$5,000" }]
    },
    {
        id: 6, name: "Battle Arena Invitational", logo: "⚡", status: "past",
        banner: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=800&q=80",
        prizePool: "$30,000", playersPerTeam: 3, game: "Rocket League",
        date: "Nov 20–22, 2025", registeredTeams: 24, maxTeams: 24,
        registerLink: "#", location: "Buenos Aires, Argentina",
        description: "Three-versus-three format invitational for top Rocket League squads.",
        editions: [
            { edition: 2, year: 2025, winner: "Boost Kings", prizePool: "$30,000" },
            { edition: 1, year: 2024, winner: "Aerial Aces", prizePool: "$20,000" },
        ]
    },
];

const EVENTS: TEvent[] = [
    { id: 1, name: "Nexus Summit 2026", flier: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&q=80", description: "Annual gathering of esports professionals, analysts, players and fans for talks, panels and networking.", date: "Apr 10, 2026", status: "upcoming", registerLink: "#", location: "São Paulo Convention Center", category: "Conference" },
    { id: 2, name: "Valo Open Day Qualifier", flier: "https://images.unsplash.com/photo-1511512578047-dfb367046420?w=600&q=80", description: "Open registration qualifier stream for the Spring Invitational Cup bracket.", date: "Mar 5, 2026", status: "upcoming", registerLink: "#", location: "Online", category: "Qualifier" },
    { id: 3, name: "NXS Live Finals Broadcast", flier: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=600&q=80", description: "Live grand finals broadcast of the Nexus World Championship at the main arena stage.", date: "Mar 10, 2026", status: "ongoing", registerLink: "#", location: "São Paulo Arena", category: "Live Event" },
    { id: 4, name: "Community Game Night", flier: "https://images.unsplash.com/photo-1560253023-3ec5d502959f?w=600&q=80", description: "Monthly casual community game night open to all players across all skill levels.", date: "Feb 15, 2026", status: "past", registerLink: "#", location: "Online", category: "Community" },
    { id: 5, name: "Draft & Strategy Workshop", flier: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=600&q=80", description: "Intensive coaching and drafting workshop led by professional analysts and coaches.", date: "Jan 28, 2026", status: "past", registerLink: "#", location: "Nexus HQ, São Paulo", category: "Workshop" },
];

const MERCH: MerchItem[] = [
    { id: 1, name: "NXS Championship Hoodie", price: 64.99, img: "https://images.unsplash.com/photo-1556821840-3a63f15732ce?w=400&q=80", badge: "New" },
    { id: 2, name: "Official Event Jersey", price: 54.99, img: "https://images.unsplash.com/photo-1620794108219-aedbde4e6fb0?w=400&q=80", badge: null },
    { id: 3, name: "NXS Snapback Cap", price: 29.99, img: "https://images.unsplash.com/photo-1588850561407-ed78c282e89b?w=400&q=80", badge: "Hot" },
    { id: 4, name: "Champion Trophy Replica", price: 149.99, img: "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=400&q=80", badge: "Limited" },
    { id: 5, name: "Gaming Mouse Pad XL", price: 24.99, img: "https://images.unsplash.com/photo-1599481238640-4c1288750d7a?w=400&q=80", badge: null },
    { id: 6, name: "NXS World Champ Tee", price: 34.99, img: "https://images.unsplash.com/photo-1571945192063-40f7fd5f7a49?w=400&q=80", badge: null },
];

const STAFF: StaffMember[] = [
    { id: 1, displayName: "Marcus Wei", role: "CEO & Founder", bio: "Visionary behind Nexus Esports with 10+ years in competitive gaming organization.", passport: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=300&q=80", fullBody: "https://images.unsplash.com/photo-1571945192063-40f7fd5f7a49?w=500&q=80", country: "🇧🇷 Brazil", state: "São Paulo", social: { instagram: "@marcus_wei", twitter: "@marcuswei_nxs" }, profileLink: "#" },
    { id: 2, displayName: "Sofia Ramos", role: "Head of Operations", bio: "Oversees all tournament logistics, staffing and venue partnerships across the portfolio.", passport: "https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=300&q=80", fullBody: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=500&q=80", country: "🇧🇷 Brazil", state: "Rio de Janeiro", social: { instagram: "@sofia_nxs", twitter: "@sofiaramos_nxs" }, profileLink: "#" },
    { id: 3, displayName: "Pedro Alvarez", role: "Tournament Director", bio: "Designs bracket formats and competitive rulesets. Former semi-pro player and analyst.", passport: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&q=80", fullBody: "https://images.unsplash.com/photo-1553778263-73a83bab9b0c?w=500&q=80", country: "🇦🇷 Argentina", state: "Buenos Aires", social: { twitter: "@pedro_td", discord: "PedroTD#1234" }, profileLink: "#" },
    { id: 4, displayName: "Aisha Okonkwo", role: "Marketing Manager", bio: "Leads brand strategy, social media and partnerships across all Nexus properties.", passport: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&q=80", fullBody: "https://images.unsplash.com/photo-1560272564-c83b66b1ad12?w=500&q=80", country: "🇳🇬 Nigeria", state: "Lagos", social: { instagram: "@aisha_nxs", twitter: "@aishaokonkwo" }, profileLink: "#" },
];

const REVIEWS: Review[] = [
    { id: 1, author: "ProGamer_BR", avatar: "https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=60&q=80", rating: 5, text: "The production quality at the Grand Prix Open was absolutely world class. Every match felt like a main stage event. Will be back every year.", tournament: "Grand Prix Open", date: "Jan 16, 2026" },
    { id: 2, author: "TeamCaptain99", avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=60&q=80", rating: 5, text: "Registration was seamless, rules were clear and the referee team was professional throughout. Nexus genuinely cares about fair competition.", tournament: "Nexus Amateur League", date: "Nov 30, 2025" },
    { id: 3, author: "StreamerKing", avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&q=80", rating: 4, text: "Amazing broadcast quality and production value. The observer work during finals was cinematic. Small delay issues in group stage though.", tournament: "Nexus World Championship", date: "Feb 26, 2026" },
    { id: 4, author: "FPS_GOAT", avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&q=80", rating: 5, text: "Spring Invitational is my favorite tournament of the year. Great prize pool, tight competition and the Nexus staff are super responsive.", tournament: "Spring Invitational Cup", date: "Sep 8, 2025" },
    { id: 5, author: "AnalystMaria", avatar: "https://images.unsplash.com/photo-1545996124-0501ebae84d0?w=60&q=80", rating: 5, text: "The Summit conference had incredible speakers and real actionable content. Best esports conference I've attended in years.", tournament: "Nexus Summit 2025", date: "Apr 15, 2025" },
    { id: 6, author: "RocketQueen", avatar: "https://images.unsplash.com/photo-1552058544-f2b08422138a?w=60&q=80", rating: 4, text: "Battle Arena Invitational was fire. Wish the format allowed more teams but the double-elimination playoffs were perfect.", tournament: "Battle Arena Invitational", date: "Nov 23, 2025" },
];

const PAGE_IDS: PageId[] = ["home", "tournaments", "events", "blog", "merch", "about"];
const PAGE_LABELS: Record<PageId, string> = {
    home: "Home", tournaments: "Tournaments", events: "Events", blog: "Blog",
    merch: "Merchandise", about: "About", dashboard: "Dashboard",
};
const SPONSORS: string[] = ORG.sponsors;

export interface LandingProps {
    publicId: string;
    basePath: string;
    website: IHostWebsite;
}

function Landing(props: LandingProps) {

    const { website, basePath } = props;
    const mainLabels = i18nManager.Labels;
    const today = DateHelper.getStartOfDay();
    const ddRef = React.useRef<HTMLDivElement>(null);
    const nameSplit = website.host.name?.split(/\s+/);
    const [nameFirstWord, ...nameRemain] = nameSplit;
    const [cart, setCart] = React.useState<any[]>([]);
    const [showDropdown, setShowDropdown] = React.useState<boolean>(false);
    const [searchParams, _] = ReactRouterDom.useSearchParams(window.location.search);
    const [profile, setProfile] = React.useState<IMember | undefined>(CacheManager.get(CacheManager.PROFILE_KEY) as IMember);
    const colors = searchParams.get("ds_colors")?.length ? JSON.parse(searchParams.get("ds_colors") ?? "{}") : (website.design_settings.scheme_settings?.colors ?? {});
    useComponentStyle(`
            :root {
                --red: ${colors.red ?? "#EF4444"};
                --dark: ${colors.dark ?? "#0A0A0F"};
                --text: ${colors.text ?? "#E8E8F0"};
                --gold: ${colors.gold ?? "#C9A84C"};
                --green: ${colors.green ?? "#22C55E"};
                --dark2: ${colors.dark2 ?? "#111118"};
                --dark3: ${colors.dark3 ?? "#1A1A24"};
                --dark4: ${colors.dark4 ?? "#22222E"};
                --muted: ${colors.muted ?? "#7A7A96"};
                --accent: ${colors.accent ?? "#4F6EF7"};
                --orange: ${colors.orange ?? "#F97316"};
                --gold-light: ${colors.gold_light ?? "#E8C96A"};
                --header-h: 72px;
            }
        `);

    React.useEffect(() => {
        Utils.updateMeta({
            title: website.host.name,
            logo: website.host.logo_url ?? website.media?.LOGO?.[0]
        });
    }, [website]);

    React.useEffect(() => {
        const h = (e: MouseEvent): void => {
            if (ddRef.current && !ddRef.current.contains(e.target as Node)) setShowDropdown(false);
        };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);

    return (<>
        <header className={styles["hdr"]}>
            <ReactRouterDom.NavLink to={`/${basePath}`} className={styles["hdr-logo-wrap"]} style={{ textDecoration: "none" }}>
                <img className={styles["hdr-org-img"]} src={website.host.logo_url ?? website.media?.LOGO?.[0]} alt={website.host.name} />
                <div className={styles["hdr-logo-text"]}>
                    {nameFirstWord} <span>{nameRemain.join(" ")}</span>
                    <span className={styles["logo-badge"]}>{nameSplit.map((s) => s[0]).join("")}</span>
                </div>
            </ReactRouterDom.NavLink>

            <nav className={styles["hdr-nav"]}>
                {Utils.entriesInCustomOrder(website.website_pages, ["HOME", "MERCH", "TOURNAMENTS", "EVENTS", "BLOG", "ABOUT"]).filter(([_, pageSettings]) => pageSettings.visible).map(([page, pageSettings]) => (
                    <ReactRouterDom.NavLink end key={page} to={{ pathname: `/${basePath}${page === "HOME" ? "" : "/" + page.toLowerCase()}`, search: searchParams.toString() }} className={({ isActive }) => Classname.build(styles[`nav-btn`], { [styles["active"]]: isActive })}>{pageSettings.label}</ReactRouterDom.NavLink>
                ))}
            </nav>

            <div className={styles["hdr-right"]}>
                {cart.length > 0 && (
                    <span style={{ fontSize: "0.8rem", color: "var(--gold)" }}>🛒 {cart.length}</span>
                )}
                <LanguageDropdown />
                {(website.extra_data.enable_visitor_account_management) && ((!profile) ? (
                    <button className={styles["btn-signin"]} onClick={() => Utils.signIn()}>Sign In</button>
                ) : (
                    <div className={styles["dropdown-wrap"]} ref={ddRef}>
                        <img className={styles["avatar-btn"]} onClick={() => setShowDropdown((v: boolean) => !v)} src={profile.profile_picture_url} alt={profile.username} />
                        {showDropdown && (
                            <div className={styles["dropdown"]}>
                                <ReactRouterDom.NavLink target='_blank' to={`${process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}`} className={({ }) => Classname.build(styles["dd-item"])}>📊 Dashboard</ReactRouterDom.NavLink>
                                <ReactRouterDom.NavLink target='_blank' to={`${process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}/teams`} className={({ }) => Classname.build(styles["dd-item"])}>⚙️ My Teams</ReactRouterDom.NavLink>
                                <ReactRouterDom.NavLink target='_blank' to={`${process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}/tournaments?section=REGISTERED`} className={({ }) => Classname.build(styles["dd-item"])}>🏆 My Tournaments</ReactRouterDom.NavLink>
                                <ReactRouterDom.NavLink target='_blank' to={`${process.env.REACT_APP_OMNEHOST_SUPPORT_URL}`} className={({ }) => Classname.build(styles["dd-item"])}>🎧 Support</ReactRouterDom.NavLink>
                                <hr className={styles["dd-divider"]} />
                                <button className={`${styles["dd-item"]} ${styles["logout"]}`} onClick={() => {
                                    CacheManager.clear();
                                    setProfile(undefined);
                                }}>← Sign Out</button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </header>

        <main className={styles["page"]}>
            <ReactRouterDom.Routes>
                <ReactRouterDom.Route path="/" element={<Home website={website} basePath={basePath} />} />
                <ReactRouterDom.Route path="/blog" element={<Blog website={website} />} />
                <ReactRouterDom.Route path="/about" element={<About website={website} />} />
                <ReactRouterDom.Route path="/events" element={<Events website={website} />} />
                <ReactRouterDom.Route path="/tournaments" element={<Tournaments website={website} />} />
            </ReactRouterDom.Routes>
        </main>

        {/* ── FOOTER ── */}
        <footer className={styles["footer"]}>
            <div className={styles["footer-inner"]}>
                <div>
                    <div className={styles["footer-logo"]}>{website.owns_copyright ? website.host.name : mainLabels.omnehost}</div>
                    <p className={styles["footer-desc"]}>
                        {website.owns_copyright ? "" : mainLabels.omnehost_detail}
                    </p>
                </div>
                <div>
                    <div className={styles["footer-col-title"]}>Navigation</div>
                    <ul className={styles["footer-links"]}>
                        {Utils.entriesInCustomOrder(website.website_pages).filter(([_, pageSettings]) => pageSettings.visible).map(([page, pageSettings]) => (<li key={page}>
                            <ReactRouterDom.NavLink end to={{ pathname: `/${basePath}${page === "HOME" ? "" : "/" + page.toLowerCase()}`, search: searchParams.toString() }} className={({ isActive }) => Classname.build({ [styles["active"]]: isActive })}>{pageSettings.label}</ReactRouterDom.NavLink>
                        </li>))}
                    </ul>
                </div>
                <div>
                    <div className={styles["footer-col-title"]}>Connect</div>
                    <ul className={styles["footer-links"]}>
                        {Object.entries(website.social_accounts ?? {}).map(([social, username]) => (<li key={social}>
                            <ReactRouterDom.NavLink target="_blank" to={Utils.getSocialLink(social, username)}>{StringHelper.toSaneSentenceFormat(social)}</ReactRouterDom.NavLink>
                        </li>))}
                    </ul>
                </div>
                {(!website.owns_copyright) && (<div>
                    <div className={styles["footer-col-title"]}>{mainLabels.omnehost}</div>
                    <ul className={styles["footer-links"]}>
                        <li>
                            {(!!profile)
                                ? (<ReactRouterDom.NavLink target="_blank" to={process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}>{mainLabels.auth.dashboard}</ReactRouterDom.NavLink>)
                                : (<ReactRouterDom.NavLink target="_blank" to={process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}>{mainLabels.auth.sign_in}</ReactRouterDom.NavLink>)}
                        </li>
                        {mainLabels.footer.map((f) => <li key={f.label}><ReactRouterDom.NavLink key={f.label} target="_blank" to={f.location}>{f.label}</ReactRouterDom.NavLink></li>)}
                    </ul>
                </div>)}
            </div>

            <div className={styles["footer-bottom"]}>
                <span>© {today.getFullYear()} {website.owns_copyright ? website.host.name : mainLabels.omnehost}. {mainLabels.landing.all_r_reserved}</span>
                <div className={styles["footer-socials"]}>
                    {Object.entries(website.owns_copyright ? (website.social_accounts ?? {}) : Utils.OMNEHOST_SOCIALS).map(([social, username]) => (
                        <ReactRouterDom.NavLink key={social} target="_blank" to={Utils.getSocialLink(social, username)} className={styles["footer-social-btn"]}><i className={Utils.getSocialIcon(social)} /></ReactRouterDom.NavLink>
                    ))}
                </div>
            </div>
        </footer>
    </>);
}

export default Landing;